import { memo, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Progress } from '@/components/ui/progress.tsx'

import { AttachmentViewer } from '@/components/attachment/AttachmentViewer.tsx'
import { DocumentThumbnail } from '@/components/attachment/DocumentThumbnail.tsx'
import { ImageThumbnail } from '@/components/attachment/ImageThumbnail.tsx'
import { AttachmentDeleteButton } from '@/components/form/AttachmentDeleteButton.tsx'
import { useAttachment } from '@/contexts/AttachmentContext.tsx'
import { formatBytes } from '@/lib/utils.ts'

type AttachmentProps = {
  attachment: Attachment
  progress?: number
  onRemove: () => void
  onDownload?: () => void
  isEditMode?: boolean
}

const UploadProgress = memo(({ progress }: { progress?: number }) => (
  <>
    {progress !== undefined ? (
      <Progress value={progress} max={100} aria-valuenow={progress} aria-valuetext={`${progress}%`} />
    ) : null}
  </>
))

export const Attachment = ({ attachment, progress, onRemove, onDownload, isEditMode }: AttachmentProps) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const { attachments } = useAttachment()

  const imageUrls = attachments
    .filter((att) => att.thumbnailable)
    .map((att) => `/api/v1/attachments/${att.id}?inline=true&type=big`)

  const selectedIndex = imageUrls.findIndex((url) => url.includes(attachment.id))

  const toggleImageViewer = () => {
    setIsViewerOpen((prevState) => !prevState)
  }

  const disablePreviewClick = onDownload === undefined

  return (
    <>
      <div className="flex bg-surface-backgroundElevated rounded-xs border border-border-default-neutral my-1 w-[300px]">
        <div className="flex flex-grow overflow-hidden">
          {attachment.thumbnailable ? (
            <ImageThumbnail
              srcImage={`/api/v1/attachments/${attachment.id}?inline=true&type=big`}
              onClick={toggleImageViewer}
              disableClick={disablePreviewClick}
            />
          ) : (
            <DocumentThumbnail
              url={`/api/v1/attachments/${attachment.id}?inline=true`}
              disableClick={disablePreviewClick}
              contentType={attachment.contentType}
            />
          )}
          <div className="flex flex-1 flex-col gap-1 p-2 truncate">
            <p className="font-EN-KR-caption-regular text-contents-tertiary truncate">{attachment.filename}</p>
            <p className="font-EN-KR-caption-regular text-contents-disabled">{`${formatBytes(attachment.size)} ${attachment.contentType}`}</p>
            <UploadProgress progress={progress} />
          </div>
        </div>
        <div className="flex items-center">
          {isEditMode && <AttachmentDeleteButton attachment={attachment} progress={progress} onRemove={onRemove} />}
          {onDownload && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onDownload}
              aria-label={`Download ${attachment.filename}`}
            >
              <img src="/images/download-icon.svg" alt="download" />
            </Button>
          )}
        </div>
      </div>
      {isViewerOpen && <AttachmentViewer images={imageUrls} initialIndex={selectedIndex} onClose={toggleImageViewer} />}
    </>
  )
}
